<template>
  <DialogHeader>
    <template #right>
      <div
        v-if="!loading"
        class="font-medium text-green-900 cursor-pointer"
        @click="handleSubmit"
      >
        Save
      </div>
      <Icon
        v-else
        name="Loading"
        :size="14"
        class="stroke-gray-800 animate-spin"
      />
    </template>
    Update your username
  </DialogHeader>
  <DialogContent>
    <div class="mb-2">
      <UiInput v-model="username" label="Username" />
    </div>
    <div
      v-if="showValidation"
      class="font-primary font-medium text-[14px] leading-[18px] flex items-center"
    >
      <template v-if="checkingUsernameAvailability">
        <Spinner :size="12" class="mr-1.5" />
        <div class="font-secondary">Checking availability</div>
      </template>
      <template v-else-if="isUsernameAvailable">
        <Icon :size="14" name="CircleCheck" class="stroke-green-900 mr-1.5" />
        <div class="font-secondary text-green-900">Username available</div>
      </template>
      <template v-else>
        <Icon :size="14" name="Alert" class="stroke-red-700 mr-1.5" />
        <div class="font-secondary text-red-700">Username not available</div>
      </template>
    </div>
  </DialogContent>
  <DialogFooter>
    <UiButton
      class="w-1/2 tablet-h:w-auto mr-2.5 tablet-h:mr-2"
      @click="close()"
    >
      Cancel
    </UiButton>
    <UiButton
      class="w-1/2 tablet-h:w-auto"
      primary
      :disabled="shouldDisableButton"
      :loading="loading"
      @click="handleSubmit"
    >
      Update username
    </UiButton>
  </DialogFooter>
</template>

<script setup lang="ts">
import { UiButton, UiInput, Icon } from '@dacr/ui'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import Spinner from '@/shared/components/Spinner.vue'
import { computed, ref, watch, watchEffect } from 'vue'
import {
  useCheckUsernameAvailabilityLazyQuery,
  useUpdateAccountUsernameMutation,
  type User,
} from '@/shared/graphql/legacy/generated'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { NativeActionsEnum, useWebviewStore } from '@/shared/stores/webview'

const { close } = useDialog()
const toastStore = useToastStore()
const webviewStore = useWebviewStore()

const props = defineProps<{
  user: User
}>()

const username = ref(props.user.username)

const variables = computed(() => ({
  username: username.value,
}))

const {
  result,
  load: checkUsernameAvailability,
  loading: checkingUsernameAvailability,
} = useCheckUsernameAvailabilityLazyQuery(variables, {
  fetchPolicy: 'network-only',
})

const { mutate: updateAccountUsername, loading } =
  useUpdateAccountUsernameMutation()

const isUsernameAvailable = ref(true)
const showValidation = computed(() => {
  return username.value.trim().length && username.value !== props.user.username
})

watch(result, () => {
  isUsernameAvailable.value = !!result.value?.checkUsernameAvailability
})

watchEffect(async () => {
  if (username.value) {
    checkUsernameAvailability()
  }
})

const shouldDisableButton = computed(
  () =>
    !isUsernameAvailable.value ||
    !showValidation.value ||
    checkingUsernameAvailability.value
)

const handleSubmit = async () => {
  if (!isUsernameAvailable.value) return

  updateAccountUsername(
    {
      username: username.value,
    },
    {
      optimisticResponse: {
        __typename: 'Mutation',
        updateAccountUsername: {
          __typename: 'User',
          id: props.user.id,
          username: username.value,
        },
      },
      update() {
        if (webviewStore.isActive) {
          webviewStore.postMessage({
            type: NativeActionsEnum.UserUpdated,
            data: {
              user: {
                ...props.user,
                username: username.value,
              },
            },
          })
        }
      },
    }
  )

  toastStore.showToast({
    type: ToastType.Success,
    content: 'Your username has been updated',
  })

  close()
}
</script>
